import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Box,
} from '@mui/material';
import { styled } from '@mui/material/styles';

const StyledTableContainer = styled(TableContainer)(({ theme }) => ({
  maxHeight: 500,
  backgroundColor: theme.custom.background1,
}));

const StyledTableHead = styled(TableHead)(({ theme }) => ({
  backgroundColor: theme.custom.background2,
  '& .MuiTableCell-head': {
    fontWeight: 'bold',
    color: theme.custom.text,
    borderBottom: `2px solid ${theme.custom.stroke}`,
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '&:nth-of-type(odd)': {
    backgroundColor: theme.custom.background1,
  },
  '&:nth-of-type(even)': {
    backgroundColor: theme.custom.background2,
  },
  '&:hover': {
    backgroundColor: theme.custom.background3,
  },
}));

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  color: theme.custom.text,
  borderBottom: `1px solid ${theme.custom.stroke}`,
  maxWidth: 300,
  wordWrap: 'break-word',
}));

const ValueCell = styled(Box)(({ theme, isOld }) => ({
  padding: '8px',
  borderRadius: '4px',
  backgroundColor: isOld ? theme.custom.background3 : theme.custom.primaryLight,
  fontFamily: 'monospace',
  fontSize: '0.875rem',
  whiteSpace: 'pre-wrap',
  wordBreak: 'break-word',
}));

/**
 * Modal component to display changed fields in a table
 * @param {Object} props
 * @param {boolean} props.open - Whether the modal is open
 * @param {Function} props.onClose - Function to close the modal
 * @param {Array} props.changes - Array of changes with fieldName, oldValue, newValue
 * @param {string} props.title - Modal title
 * @param {boolean} props.isConfirmation - Whether this is a confirmation modal (shows confirm/cancel buttons)
 * @param {Function} props.onConfirm - Function to call when user confirms (only for confirmation modals)
 * @param {boolean} props.loading - Whether the confirm action is loading
 */
const ChangesModal = ({
  open,
  onClose,
  changes = [],
  title = 'Changes Overview',
  isConfirmation = false,
  onConfirm,
  loading = false,
}) => {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      aria-labelledby="changes-modal-title"
    >
      <DialogTitle id="changes-modal-title">{title}</DialogTitle>
      <DialogContent>
        {changes.length === 0 ? (
          <Typography variant="body1" sx={{ py: 2 }}>
            No changes detected.
          </Typography>
        ) : (
          <>
            {isConfirmation && (
              <Typography
                variant="body2"
                color="textSecondary"
                sx={{ mb: 2, mt: 1 }}
              >
                The following changes will be applied:
              </Typography>
            )}
            <StyledTableContainer component={Paper}>
              <Table stickyHeader>
                <StyledTableHead>
                  <TableRow>
                    <StyledTableCell width="25%">Field</StyledTableCell>
                    <StyledTableCell width="37.5%">Old Value</StyledTableCell>
                    <StyledTableCell width="37.5%">New Value</StyledTableCell>
                  </TableRow>
                </StyledTableHead>
                <TableBody>
                  {changes.map((change, index) => (
                    <StyledTableRow key={index}>
                      <StyledTableCell component="th" scope="row">
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: 'medium' }}
                        >
                          {change.fieldName}
                        </Typography>
                      </StyledTableCell>
                      <StyledTableCell>
                        <ValueCell isOld={true}>{change.oldValue}</ValueCell>
                      </StyledTableCell>
                      <StyledTableCell>
                        <ValueCell isOld={false}>{change.newValue}</ValueCell>
                      </StyledTableCell>
                    </StyledTableRow>
                  ))}
                </TableBody>
              </Table>
            </StyledTableContainer>
          </>
        )}
      </DialogContent>
      <DialogActions>
        {isConfirmation ? (
          <>
            <Button onClick={onClose} disabled={loading}>
              Cancel
            </Button>
            <Button
              onClick={onConfirm}
              variant="contained"
              color="primary"
              disabled={loading || changes.length === 0}
            >
              {loading ? 'Saving...' : 'Confirm & Save'}
            </Button>
          </>
        ) : (
          <Button onClick={onClose} variant="contained" color="primary">
            Close
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ChangesModal;
